/**
 * @fileoverview A button or link action for the menu component
 */
goog.declareModuleId('yext.ui.components.menu.action.MenuAction');

import {Action} from '/ui/components/menu/types/action';
import {classNames} from '/ui/lib/classnames';
import {useUid} from '/ui/lib/uid';

import * as styles from '/ui/components/menu/menu.module.scss';
import * as selectStyles from '/ui/components/select/standard/standard.module.scss';
import * as a11yStyles from '/ui/utilities/a11y.module.scss';

/**
 * The original MenuAction, kept around for backwards compatibility as there are
 * several one-off menu implementations using it directly. This will not be
 * updated with new features.
 * @deprecated If Menu is missing features needed for your use case, please let Aurora know.
 * @param {{
 *   action: !Action,
 *   classList: !Array<string>,
 *   onCloseFunc?: (function(): void),
 * }} props
 * @returns {React.ReactElement}
 */
export function MenuAction({
  action: {
    name,
    displayName,
    callback,
    href,
    disabled,
    openNewTab,
    tid,
    secondaryText,
    id,
    pendoId,
    label,
  },
  classList,
  onCloseFunc,
}) {
  const uid = useUid();

  if (callback) {
    return (
      <>
        {label && <label htmlFor={id || uid} className={a11yStyles.srOnly}>
          {label}
        </label>}
        <button
          type="button"
          className={`ui-menu-action ui-menu-buttonaction ${classList.join(' ')}`}
          onClick={() => callbackWrapper(callback, onCloseFunc)}
          disabled={disabled}
          id={id || uid}
          tid={tid}
          data-pendo={pendoId}
        >
          {displayName ?? name}
          {secondaryText
          && <div className={styles.secondaryText}>{secondaryText}</div>}
        </button>
      </>
    );
  } else if (href) {
    const target = openNewTab ? '_blank' : undefined;
    const rel = openNewTab ? 'noopener noreferrer' : undefined;

    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={`ui-menu-action ui-menu-linkaction ${classList.join(' ')}`}
        tid={tid}
        aria-label={label ? label: ''}
        data-pendo={pendoId}
        onKeyDown={keydownHandler}
      >
        {name}
        {secondaryText
        && <div className={styles.secondaryText}>{secondaryText}</div>}
      </a>
    );
  }
  throw new Error('Provided action missing callback or href');
}

/**
 * New MenuAction component. This should not be used outside of the official
 * Menu component.
 * @param {{
 *   action: !Action,
 *   onCloseFunc?: (function(): void),
 * }} props
 * @returns {React.ReactElement}
 */
export function MenuAction2({
  action: {
    name,
    displayName,
    callback,
    href,
    actions,
    disabled,
    destructive,
    openNewTab,
    tid,
    pendoId,
    secondaryText,
    icon,
    id,
    label,
  },
  onCloseFunc,
}) {
  const commonStyles = [
    selectStyles.selectLabelContainer,
    'js-select-target',
    {
      [styles.itemDisabled]: disabled,
      [styles.itemDestructive]: destructive,
    },
  ];
  const labelContent = <>
    {icon && <span className={selectStyles.selectOptionIcon}>{icon}</span>}
    <span>
      {displayName ?? name}
      {secondaryText && <span className={styles.secondaryText}>{secondaryText}</span>}
    </span>
  </>;
  if (callback) {
    const className = classNames(
      commonStyles,
      styles.buttonAction,
      'ui-menu-action', // used by gocode/src/yext/publish/sitesdirectory/storm/acceptance/smoke/directorylist.test.ts
      'ui-menu-buttonaction', // used by test/com/yext/dataingestionstorm/acceptance/blocks/ConnectorAddDataPage.java
      'js-menu-target', // used by src/com/yext/analytics/analyticsstorm/app/js/tests/reportssidebar/{reportssidebardropdown,sidepanel_IT}.test.jsx
    );
    return (
      <button
        type="button"
        onClick={() => callbackWrapper(callback, onCloseFunc)}
        disabled={disabled}
        aria-label={label}
        className={className}
        id={id}
        tid={tid}
        data-pendo={pendoId}
      >
        {labelContent}
      </button>
    );
  } else if (href) {
    const target = openNewTab ? '_blank' : undefined;
    const rel = openNewTab ? 'noopener noreferrer' : undefined;
    const className = classNames(
      commonStyles,
      styles.linkAction,
      'ui-menu-action', // used by gocode/src/yext/publish/sitesdirectory/storm/acceptance/smoke/directorylist.test.ts
      'js-menu-target', // used by src/com/yext/analytics/analyticsstorm/app/js/tests/reportssidebar/{reportssidebardropdown,sidepanel_IT}.test.jsx
    );

    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onKeyDown={keydownHandler}
        aria-label={label}
        className={className}
        id={id}
        tid={tid}
        data-pendo={pendoId}
      >
        {labelContent}
      </a>
    );
  } else if (actions) {
    const className = classNames(
      commonStyles,
      styles.parentAction,
    );
    return (
      <div
        tabIndex={disabled ? -1 : 0}
        className={className}
        id={id}
        tid={tid}
        data-pendo={pendoId}
      >
        {labelContent}
      </div>
    );
  }
  throw new Error('Provided action missing callback, href, or actions');
}

function keydownHandler(e) {
  if (e.key === ' ' || e.key === 'Spacebar') {
    e.preventDefault();
    e.target.click();
  }
}

function callbackWrapper(callback, onCloseFunc) {
  callback();

  if (onCloseFunc) {
    onCloseFunc();
  }
}
